import {
  type FillLayerSpecification,
  type LineLayerSpecification,
  type StyleSpecification,
} from '@aus-platform/cesium';
import { type SelectOption } from '@aus-platform/design-system';
import { type ImageryLayer } from 'cesium';
import { cloneDeep, isEmpty } from 'lodash';
import type { AoiManager } from 'map-3d/shared';
import { FeatureType } from 'shared/api';
import { type CesiumProxy } from 'shared/cesium';
import { type WorkspaceLayer } from 'map-3d/components';
import { lineLayersTemplate, polygonLayersTemplate } from './constants';
import { type WorkspaceZoneType } from './enums';

/**
 * Clones the appropriate style layer templates and sets the given source ID.
 *
 * @param sourceId - The ID to assign as the `source` in each style layer
 * @param geometryType - Feature type (Polygon or LineString) to determine which template to use
 * @returns Array of style layers with updated source
 */
export const createStyleLayers = (
  sourceId: string,
  geometryType: FeatureType = FeatureType.Polygon,
): (FillLayerSpecification | LineLayerSpecification)[] => {
  const templates =
    geometryType === FeatureType.LineString
      ? lineLayersTemplate
      : polygonLayersTemplate;

  return templates.map(
    (templateLayer: FillLayerSpecification | LineLayerSpecification) => {
      const cloned = cloneDeep(templateLayer);
      cloned.source = sourceId;
      return cloned;
    },
  );
};

/**
 * Removes previously loaded Cesium layers that are no longer selected in the UI for a given zone.
 *
 * For each imagery layer:
 * - If the layer’s `zoneType` matches the provided `zoneType`
 * - And its ID is not present in the currently selected layer IDs
 * → It will be removed from the Cesium map and from the loaded tracking set.
 *
 * @param zoneType - WorkspaceZoneType (e.g. Excavation, Dump, SectionLine)
 * @param selectedIds - The currently selected layer IDs
 * @param loadedLayerIds - Tracks which layer IDs have already been added to Cesium
 * @param cesiumProxy - CesiumProxy
 */
export const removeUnselectedLayersFromCesium = (
  zoneType: WorkspaceZoneType,
  selectedIds: Set<string>,
  loadedLayerIds: Set<string>,
  cesiumProxy: CesiumProxy,
  aoiManager: AoiManager | null,
): void => {
  const imageryLayers = cesiumProxy?.layerManager?.getImageryLayers();
  if (!imageryLayers) {
    return;
  }

  const layersToRemove: ImageryLayer[] = [];

  for (let index = 0; index < imageryLayers.length; index++) {
    const layer = imageryLayers.get(index);
    const layerId = layer['layerId'];

    if (
      layerId &&
      layer['zoneType'] === zoneType &&
      !selectedIds.has(layerId)
    ) {
      layersToRemove.push(layer);
      loadedLayerIds.delete(layerId);
    }
  }

  if (!isEmpty(layersToRemove)) {
    layersToRemove.forEach((layer) => {
      aoiManager?.removeImageryLayer(layer);
    });
    cesiumProxy.layerManager.removeImageryLayers(layersToRemove);
  }
};

/**
 * Loads selected workspace layers into Cesium if they haven't been added yet.
 *
 * For each new layer:
 * 1. Builds the correct style layers (Polygon or LineString) using the source ID.
 * 2. Clones and prepares a styled WorkspaceLayer with those layers.
 * 3. Adds the vector tile layer to Cesium using the provided proxy.
 * 4. Tags the layer with `layerId` and `zoneType` for identification.
 * 5. Adds the layer ID to the tracking Set to avoid duplicate loads.
 *
 * @param zoneType - WorkspaceZoneType (e.g. Excavation, Dump, SectionLine)
 * @param selectedLayers - The currently selected layers
 * @param loadedLayerIds - Tracks which layer IDs are already added to Cesium
 * @param cesiumProxy - CesiumProxy
 * @param geometryType -FeatureType (default: Polygon)
 */
export const addMissingLayersToCesium = async (
  zoneType: WorkspaceZoneType,
  selectedLayers: SelectOption<WorkspaceLayer>[],
  loadedLayerIds: Set<string>,
  cesiumProxy: CesiumProxy,
  aoiManager: AoiManager | null,
  geometryType = FeatureType.Polygon,
): Promise<void> => {
  for (const { value: layer } of selectedLayers) {
    if (!loadedLayerIds.has(layer.id)) {
      const sourceId =
        Object.keys(layer.mapLayerStyles?.sources ?? {})[0] ?? '';

      const configuredLayers = createStyleLayers(sourceId, geometryType);

      const styledLayer: WorkspaceLayer = {
        ...layer,
        mapLayerStyles: {
          ...layer.mapLayerStyles,
          layers: configuredLayers,
        } as StyleSpecification,
      };

      try {
        const imageryLayer =
          await cesiumProxy.layerManager?.addVectorTiledLayerToCesium(
            styledLayer,
            { show: true, zIndex: styledLayer.zIndex },
          );

        if (imageryLayer) {
          imageryLayer['layerId'] = layer.id;
          imageryLayer['zoneType'] = zoneType;
          loadedLayerIds.add(layer.id);
          aoiManager?.addImageryLayer(imageryLayer);
        }
      } catch (error) {
        console.error(`Failed to add ${zoneType} layer ${layer.name}:`, error);
      }
    }
  }
};
